// src/components/Navbar.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WriterAuth from './WriterAuth';
import WriterSettings from './WriterSettings';
import { CASE_STUDY_NAV } from '../pages/caseStudiesData';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsDropdown, setSolutionsDropdown] = useState(false);
  const [caseStudiesDropdown, setCaseStudiesDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const location = useLocation();
  
  const { currentUser, isWriter, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      setUserDropdown(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getDisplayName = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName.split(' ')[0];
    }
    return currentUser?.email?.split('@')[0] || 'User';
  };

  return (
    <>
      <nav className="nav">
        <div className="nav-container">
          <Link to="/home" className="nav-logo">
            <div className="logo-icon"><img src="/avenirlogowhite.png" alt="Avenir AI" /></div>
            <span className="logo-text">Avenir AI</span>
          </Link>
          
          <div className="nav-links">
            <Link to="/product" className={location.pathname === '/product' ? 'active' : ''}>
              Product
            </Link>
            
            <div 
              className="nav-dropdown"
              onMouseEnter={() => setSolutionsDropdown(true)}
              onMouseLeave={() => setSolutionsDropdown(false)}
            >
              <span className="nav-dropdown-trigger">
                Solutions
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
              {solutionsDropdown && (
                <div className="nav-dropdown-menu">
                  <Link to="/solutions/employers">For Employers</Link>
                  <Link to="/solutions/brokers">For Brokers & Consultants</Link>
                  <Link to="/solutions/cfos">For CFOs</Link>
                </div>
              )}
            </div>
            
            <div
              className="nav-dropdown"
              onMouseEnter={() => setCaseStudiesDropdown(true)}
              onMouseLeave={() => setCaseStudiesDropdown(false)}
            >
              <span className={`nav-dropdown-trigger ${location.pathname.startsWith('/case-studies') ? 'active' : ''}`}>
                Case Studies
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
              {caseStudiesDropdown && (
                <div className="nav-dropdown-menu">
                  {CASE_STUDY_NAV.map((c) => (
                    <Link key={c.to} to={c.to}>{c.label}</Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blog" className={location.pathname.startsWith('/blog') ? 'active' : ''}>
              Blog
            </Link>

            <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>
              About Us
            </Link>
          </div>

          <div className="nav-actions">
            {/* Write Button (for writers only) */}
            {isWriter && (
              <Link to="/blog/new" className="write-btn">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Write
              </Link>
            )}

            {/* User Menu or Sign In */}
            {currentUser ? (
              <div 
                className="user-menu"
                onMouseEnter={() => setUserDropdown(true)}
                onMouseLeave={() => setUserDropdown(false)}
              >
                <button className="user-btn">
                  {currentUser.photoURL ? (
                    <img src={currentUser.photoURL} alt={getDisplayName()} className="user-avatar-img" />
                  ) : (
                    <div className="user-avatar">
                      {getDisplayName().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="user-name">
                    Welcome, {getDisplayName()}
                  </span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {userDropdown && (
                  <div className="user-dropdown">
                    {isWriter && (
                      <>
                        <Link to="/blog/new">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          Write Article
                        </Link>
                        <div className="dropdown-divider"></div>
                      </>
                    )}
                    <button onClick={() => { setShowSettings(true); setUserDropdown(false); }}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </button>
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="text-red">
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                className="sign-in-btn"
                onClick={() => setShowAuthModal(true)}
              >
                Sign In
              </button>
            )}

            <Link to="/contact" className="btn btn-primary">Talk With Us</Link>
          </div>
          
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link to="/product" onClick={() => setMobileMenuOpen(false)}>Product</Link>
            <Link to="/solutions/employers" onClick={() => setMobileMenuOpen(false)}>For Employers</Link>
            <Link to="/solutions/brokers" onClick={() => setMobileMenuOpen(false)}>For Brokers</Link>
            <Link to="/solutions/cfos" onClick={() => setMobileMenuOpen(false)}>For CFOs</Link>
            {CASE_STUDY_NAV.map((c) => (
              <Link key={c.to} to={c.to} onClick={() => setMobileMenuOpen(false)}>{c.label}</Link>
            ))}
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
            {currentUser ? (
              <>
                {isWriter && (
                  <Link to="/blog/new" onClick={() => setMobileMenuOpen(false)}>Write Article</Link>
                )}
                <button onClick={() => { setShowSettings(true); setMobileMenuOpen(false); }}>
                  Settings
                </button>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                  Sign Out
                </button>
              </>
            ) : (
              <button className="sign-in-btn" onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}>
                Sign In
              </button>
            )}
            <Link to="/contact" className="btn btn-primary" onClick={() => setMobileMenuOpen(false)}>
              Talk With Us
            </Link>
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <WriterAuth 
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <WriterSettings onClose={() => setShowSettings(false)} />
      )}
    </>
  );
};

export default Navbar;